import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MdVerified } from "react-icons/md";
import InstagramGlyph from "./InstagramGlyph";
import { AuthContext } from "../context/AuthContext";
import { usersApi } from "../services/users";
import { useLinkFlag } from "../hooks/useLinkFlag";
import { closeMoneyGate, onMoneyGate, openReviewSheet } from "../lib/connectBank";
import { cleanInstagramHandle, cleanLink } from "./QrGateCard";
import TalentIgConnect from "./TalentIgConnect";
import { startInstagramLogin } from "../lib/instagramLogin";
import { absMediaUrl } from "../utils/mediaUrl";
import type { User } from "../types/User";
import "./ConnectBankSheet.css";
import "./ReviewSheet.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

// Free tier: the second money-gate sheet (lib/connectBank) — the bank is
// connected, the founder has not approved yet. Same words as the QR card it
// grew out of (components/QrGateCard, founder wording 2026-09-19), in the
// bottom sheet every money button opens.
//
//   needs_instagram  "Verify your Instagram" (or "Add something else": a link)
//   in_review        "You're in review" — Edit reopens the field
//   declined         the one dead end, so it carries the address to write to
//
// Both Instagram switches on (build 54): nothing is typed for Instagram —
// Continue (and Edit on a handle) opens Connect Instagram over the sheet
// (components/TalentIgConnect), the same screen as sign-up's step 2.
//
// A verified Instagram required to earn (founder, 2026-11-03; the server's
// lib/approval.js, profile.instagramRequiredToEarn): a link no longer gets a
// talent reviewed, so "No Instagram?" gives way to the address to write to.
const Chevron = () => (
  <svg className="cbs-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export default function ReviewSheet() {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<User | null>(null);
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<"instagram" | "link">("instagram");
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  useEffect(() => onMoneyGate((gate) => {
    const next = gate === "review";
    setOpen(next);
    if (next) { setEditing(false); setValue(""); setError(""); setSaving(false); setConnectOpen(false); }
  }), []);

  // Declared AFTER the subscription above, so the sheet is listening before
  // the link opens it.
  // The "One last step" email's Connect Instagram button lands on
  // /requests?verify=1: open this sheet straight away, then drop the flag so a
  // refresh or Back doesn't reopen it (hooks/useLinkFlag).
  useLinkFlag("verify", openReviewSheet);


  useEffect(() => {
    if (!open || !user?.slug) return;
    let alive = true;
    setProfile(null);
    usersApi.get(user.slug).then((p) => {
      if (!alive) return;
      // Already approved (an old email link): nothing to verify, close quietly.
      // No status at all means approved too (types/User: "Absent = approved").
      const status = (p as User).approvalStatus;
      if (!status || status === "approved") return closeMoneyGate();
      setProfile(p as User);
      const hasHandle = !!((p as User).instagram || "").trim();
      setMode((p as User).instagramRequiredToEarn || hasHandle || !((p as User).links || [])[0] ? "instagram" : "link");
    }).catch(() => { if (alive) closeMoneyGate(); });
    return () => { alive = false; };
  }, [open, user?.slug]);

  if (!open || !profile) return null;

  const close = () => { if (!saving) closeMoneyGate(); };
  const status = profile.approvalStatus;
  const handle = (profile.instagram || "").trim().replace(/^@+/, "");
  const firstLink = (profile.links || [])[0] || "";
  const connectMode = !!profile.instagramLoginEnabled && !!profile.instagramDmVerifyEnabled;

  // Instagram vouched (the message, inside Connect): the profile as the server
  // has it now, for this sheet and every screen under it.
  const reread = () =>
    usersApi.get(profile.slug).then((fresh) => {
      const p = fresh as User;
      setEditing(false);
      setProfile(p);
      window.dispatchEvent(new CustomEvent("profile:patched", { detail: { instagram: p.instagram, links: p.links, approvalStatus: p.approvalStatus || "approved" } }));
    });
  // Leaving Connect: approved on the spot (a big enough Instagram) means
  // nothing is left to ask here.
  const closeConnect = () => {
    setConnectOpen(false);
    if (!profile.approvalStatus || profile.approvalStatus === "approved") closeMoneyGate();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (connectMode && mode === "instagram") {
      setError("");
      setConnectOpen(true);
      return;
    }
    const cleaned = mode === "instagram" ? cleanInstagramHandle(value) : cleanLink(value);
    if (!cleaned) {
      setError(mode === "instagram" ? t("reviewSheet.thatDoesnTLook") : t("reviewSheet.thatDoesnTLook2"));
      return;
    }
    const form = new FormData();
    if (mode === "instagram") form.append("instagram", cleaned);
    // The server replaces the whole list, so keep any other links they have.
    else form.append("links", JSON.stringify([cleaned, ...(profile.links || []).slice(1)]));
    setSaving(true);
    try {
      const saved = await usersApi.update(profile.slug, form);
      const patch = { instagram: saved.instagram, links: saved.links, approvalStatus: saved.approvalStatus || "in_review" };
      setProfile({ ...profile, ...patch });
      setEditing(false);
      // Every open screen that shows the profile hears it (the price card's line).
      window.dispatchEvent(new CustomEvent("profile:patched", { detail: patch }));
    } catch (err: any) {
      // The switches came on since this sheet opened: Connect, not typing.
      if (err?.response?.data?.code === "instagram_verify_required") setConnectOpen(true);
      else setError(t("reviewSheet.thatDidnTSave"));
    } finally {
      setSaving(false);
    }
  };

  let body: React.ReactNode;
  if (status === "declined") {
    body = (
      <>
        <h2 id="rvs-title" className="cbs-title rvs-title-top"><T k="reviewSheet.weCouldnTVerify" tags={{ 0: <br /> }} /></h2>
        <p className="cbs-sub"><T k="reviewSheet.questionsHelloRequestaApp" tags={{ 0: (c) => <a className="rvs-mail" href="mailto:hello@requesta.app">{c}</a> }} /></p>
        <button type="button" className="cbs-secondary" onClick={close}>{t("reviewSheet.close")}</button>
      </>
    );
  } else if (status === "in_review" && !editing) {
    body = (
      <>
        <div className="cbs-orb-wrap" aria-hidden="true">
          <span className="cbs-orb rvs-orb-green">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
          </span>
        </div>
        <h2 id="rvs-title" className="cbs-title">{t("reviewSheet.youReInReview")}</h2>
        <p className="cbs-sub rvs-sub-tight">{t("reviewSheet.weLlNotifyYou")}</p>
        <p className="rvs-meta">
          {handle && <span className="rvs-ig-mini" aria-hidden="true"><InstagramGlyph size={18} tight /></span>}
          <span className="rvs-handle">{handle ? `@${handle}` : firstLink.replace(/^https?:\/\//, "")}</span>
          {" · "}
          <button type="button" className="rvs-link" onClick={() => { if (connectMode && handle) { setError(""); setConnectOpen(true); return; } setValue(handle || firstLink); setMode(handle ? "instagram" : "link"); setError(""); setEditing(true); }}>{t("reviewSheet.edit")}</button>
        </p>
        <button type="button" className="cbs-secondary" onClick={close}>{t("reviewSheet.gotIt")}</button>
      </>
    );
  } else {
    body = (
      <form onSubmit={submit} aria-label={t("reviewSheet.verifyYourProfile")}>
        {mode === "instagram" && (
          <div className="cbs-orb-wrap" aria-hidden="true"><span className="rvs-ig"><InstagramGlyph size={34} tight /></span></div>
        )}
        <h2 id="rvs-title" className={`cbs-title${mode === "instagram" ? "" : " rvs-title-top"}`}>
          {mode === "instagram" ? t("reviewSheet.verifyYourInstagram") : t("reviewSheet.addSomethingElse")}
          {mode === "instagram" && <MdVerified className="rvs-verified" aria-hidden />}
        </h2>
        {!(connectMode && mode === "instagram") && <label className="rvs-field">
          {mode === "instagram" && <span className="rvs-at" aria-hidden>@</span>}
          <input
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(""); }}
            placeholder={mode === "instagram" ? "username" : t("reviewSheet.facebookYoutubeSoundcloudLink")}
            aria-label={mode === "instagram" ? t("reviewSheet.instagramUsername") : t("reviewSheet.link")}
            inputMode={mode === "instagram" ? "text" : "url"}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={mode === "instagram" ? 60 : 200}
          />
        </label>}
        {mode === "instagram" && !error && <p className="rvs-hint">{t("reviewSheet.thisIsHowWe")}</p>}
        {error && <p className="cbs-error" role="alert">{error}</p>}
        <button type="submit" className="cbs-primary rvs-green" disabled={saving || (!value.trim() && !(connectMode && mode === "instagram"))}>
          <span>{saving ? t("reviewSheet.saving") : t("reviewSheet.continue")}</span>
          <Chevron />
        </button>
        {profile.instagramRequiredToEarn ? (
          <p className="rvs-ask"><T k="reviewSheet.questionsHelloRequestaApp" tags={{ 0: (c) => <a className="rvs-mail" href="mailto:hello@requesta.app">{c}</a> }} /></p>
        ) : (
          <button type="button" className="rvs-link rvs-link-block" onClick={() => { setMode(mode === "instagram" ? "link" : "instagram"); setValue(""); setError(""); }}>
            {mode === "instagram" ? t("reviewSheet.noInstagram") : t("reviewSheet.useInstagramInstead")}
          </button>
        )}
      </form>
    );
  }

  // Connect Instagram sits beside the sheet in React's tree, not inside it: a
  // tap in a portal still bubbles to its React parents, and the overlay's
  // click closes the sheet.
  return (
    <>
      {createPortal(
        <div className="cbs-overlay" onClick={close} role="presentation">
          <div className="cbs-sheet" role="dialog" aria-modal="true" aria-labelledby="rvs-title" onClick={(e) => e.stopPropagation()}>
            <span className="cbs-handle" aria-hidden="true" />
            <button type="button" className="cbs-x" onClick={close} aria-label={t("reviewSheet.close")}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            {body}
          </div>
        </div>,
        document.body
      )}
      {connectOpen && (
        <TalentIgConnect
          name={profile.name}
          avatarUrl={profile.avatarUrl ? absMediaUrl(profile.avatarUrl) : null}
          startLogin={() => startInstagramLogin("profile")}
          onVerified={() => void reread().catch(() => undefined)}
          onTaken={() => {
            // The form, with today's line (the in-review card has no room for it).
            setConnectOpen(false);
            setMode("instagram");
            setEditing(true);
            setError(t("editProfileSheet.thisInstagramIsAlready"));
          }}
          onClose={closeConnect}
        />
      )}
    </>
  );
}
